import { useEffect, useState } from "react";
import { toast } from "sonner";
import { g, p, pu } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Volume2, Play } from "lucide-react";
import { vociItaliane, sbloccaVoce, speak } from "@/lib/voce";

const PROVA = "Tavolo quattro: due paniscia e un fritto misto di mare. Comanda inviata in cucina.";
const PROVA_BREVE = "Nuova comanda, ... Tavolo quattro, ... due piatti nuovi.";

export const VoceCard = () => {
  const { settings, refresh } = useApp();
  const [voices, setVoices] = useState([]);
  const [canChoose, setCanChoose] = useState(false);
  const [canFull, setCanFull] = useState(false);
  const [sel, setSel] = useState("");
  const [motore, setMotore] = useState(settings?.voice_engine || "device");
  const [vDevice, setVDevice] = useState(settings?.voice_device || "");
  const [vociDisp, setVociDisp] = useState([]);
  const [busy, setBusy] = useState(false);
  const [prep, setPrep] = useState(null);

  useEffect(() => {
    setVociDisp(vociItaliane());
    const t = setTimeout(() => setVociDisp(vociItaliane()), 1200); // le voci arrivano in ritardo
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    g("/voices")
      .then((r) => {
        setVoices(r.voices);
        setCanChoose(!!r.can_choose);
        setCanFull(!!r.can_full);
        setSel(r.current);
      })
      .catch(() => {});
  }, []);

  if (!voices.length) return null;

  const ascolta = async (voice) => {
    setBusy(true);
    try {
      const r = await p("/tts", { text: PROVA, voice });
      await new Audio(`${process.env.REACT_APP_BACKEND_URL}${r.url}`).play();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prova vocale non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const salva = async () => {
    await pu("/settings", { ...settings, voice: sel, voice_engine: motore, voice_device: vDevice });
    await refresh();
    toast.success("Voce aggiornata", { description: "Gli annunci in cucina useranno questa voce." });
  };

  const gruppi = [
    ["Voci femminili", voices.filter((v) => v.gender === "f")],
    ["Voci maschili", voices.filter((v) => v.gender === "m")],
  ];

  return (
    <Card className="space-y-4 p-6" data-testid="voce-card">
      <div className="flex items-start gap-3">
        <Volume2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <h3 className="font-semibold">Voce degli annunci in cucina</h3>
          <p className="mt-1 text-sm text-slate-500">
            È la voce che legge le comande nei monitor di cucina, pizzeria e bar. Ascoltale e scegli quella
            che si sente meglio nel tuo locale.
          </p>
        </div>
      </div>

      <div className="space-y-3 rounded-xl border border-slate-200 p-3" data-testid="voce-motore">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Da dove arriva la voce</p>
        {[
          ["device", "Voce italiana del telefono", "Pronuncia italiana corretta, nessun costo. Usa le voci del tuo iPhone o tablet (Alice, Federica, Google italiano)."],
          ["server", "Voce registrata dal server", "Riserva per i tablet senza voci italiane installate: timbro curato ma pronuncia con accento straniero."],
        ].map(([id, titolo, nota]) => (
          <label
            key={id}
            data-testid={`voce-motore-${id}`}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${motore === id ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}
          >
            <input
              type="radio"
              name="motore"
              checked={motore === id}
              onChange={() => setMotore(id)}
              className="mt-0.5 h-4 w-4"
              data-testid={`voce-motore-radio-${id}`}
            />
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{titolo}</span>
              <span className="block text-xs text-slate-500">{nota}</span>
            </span>
          </label>
        ))}

        {motore === "device" && (
          <div data-testid="voce-device-blocco">
            {vociDisp.length ? (
              <>
                <select
                  className="h-10 w-full rounded-md border border-slate-200 px-2 text-sm"
                  value={vDevice}
                  onChange={(e) => setVDevice(e.target.value)}
                  data-testid="voce-device-select"
                >
                  <option value="">Scelta automatica (la migliore disponibile)</option>
                  {vociDisp.map((v) => (
                    <option key={v.name} value={v.name}>
                      {v.name}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  data-testid="voce-device-play"
                  onClick={async () => {
                    await sbloccaVoce();
                    speak(PROVA_BREVE, "cucina", vDevice);
                  }}
                >
                  <Play className="mr-1 h-3.5 w-3.5" /> Ascolta con campanella
                </Button>
              </>
            ) : (
              <p className="text-xs text-amber-700" data-testid="voce-device-vuoto">
                Su questo dispositivo non risultano voci italiane installate. Su Android: Impostazioni →
                Sintesi vocale → Installa voci. Su iPhone sono già presenti (Alice, Federica).
              </p>
            )}
          </div>
        )}
      </div>

      {gruppi.map(([titolo, lista]) => (
        <div key={titolo} className={lista.length && canChoose && motore === "server" ? "" : "hidden"}>
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{titolo}</p>
          <div className="mt-2 space-y-2">
            {lista.map((v) => (
              <label
                key={v.id}
                data-testid={`voce-${v.id}`}
                className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                  sel === v.id ? "border-slate-900 bg-slate-50" : "border-slate-200"
                }`}
              >
                <input
                  type="radio"
                  name="voce"
                  checked={sel === v.id}
                  onChange={() => setSel(v.id)}
                  className="h-4 w-4"
                  data-testid={`voce-radio-${v.id}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{v.label}</span>
                  <span className="block text-xs text-slate-500">{v.desc}</span>
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={(e) => {
                    e.preventDefault();
                    ascolta(v.id);
                  }}
                  data-testid={`voce-play-${v.id}`}
                >
                  <Play className="mr-1 h-3.5 w-3.5" /> Ascolta
                </Button>
              </label>
            ))}
          </div>
        </div>
      ))}

      <Button onClick={salva} disabled={busy} data-testid="voce-save-btn">
        Usa questa voce
      </Button>

      <div className={canChoose || motore === "device" ? "hidden" : "rounded-xl border border-slate-200 bg-slate-50 p-3"} data-testid="voce-fissa">
        <p className="text-sm font-semibold">Voce in uso: {voices.find((v) => v.id === sel)?.label || "predefinita"}</p>
        <p className="mt-1 text-xs text-slate-500">
          È la voce impostata per tutti i locali. Con il piano Pro puoi scegliere fra sei voci e far
          leggere anche i nomi dei piatti.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="mt-3"
          disabled={busy}
          onClick={() => ascolta(sel)}
          data-testid="voce-play-corrente"
        >
          <Play className="mr-1 h-3.5 w-3.5" /> Ascolta
        </Button>
      </div>

      <div className={motore === "device" ? "hidden" : "rounded-xl border border-slate-200 p-3"} data-testid="voce-prepara">
        <p className="text-sm font-semibold">Prepara gli annunci</p>
        <p className="mt-1 text-xs text-slate-500">
          Genera in anticipo tutte le frasi di servizio dei tuoi tavoli, così in serata la voce parte
          subito e senza consumi. Da fare una volta, e dopo ogni cambio di tavoli.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="mt-3"
          disabled={busy}
          data-testid="voce-prepara-btn"
          onClick={async () => {
            setBusy(true);
            try {
              const r = await p("/tts/prepare");
              toast.success("Preparazione avviata", {
                description: `${r.totale || prep?.totale || ""} frasi in lavorazione: puoi chiudere la pagina.`,
              });
              const tic = setInterval(async () => {
                try {
                  const s = await g("/tts/prepare/status");
                  setPrep(s);
                  if (!s.running) {
                    clearInterval(tic);
                    setBusy(false);
                    toast.success(`Annunci pronti: ${s.fatte + s.gia_pronte} frasi`, {
                      description: s.errori ? `${s.errori} non riuscite` : "Tutte in memoria, non si pagano più.",
                    });
                  }
                } catch {
                  clearInterval(tic);
                  setBusy(false);
                }
              }, 2500);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Preparazione non riuscita");
              setBusy(false);
            }
          }}
        >
          {busy ? "Sto preparando…" : "Prepara ora"}
        </Button>
        {prep && (
          <p className="font-num mt-2 text-[11px] text-slate-500" data-testid="voce-prepara-esito">
            {prep.fatte + prep.gia_pronte} su {prep.totale} frasi pronte
            {prep.errori ? ` · ${prep.errori} non riuscite` : ""}
            {prep.running ? " · in corso…" : ""}
          </p>
        )}
      </div>

      <p className="text-xs text-slate-500">
        {canFull
          ? "Con il tuo piano la voce può leggere anche i nomi dei piatti."
          : "La voce annuncia il tavolo e quante portate sono arrivate: i piatti si leggono sullo schermo."}{" "}
        Le frasi già lette restano salvate e non vengono rigenerate.
      </p>
    </Card>
  );
};
